import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaChevronDown, FaExternalLinkAlt } from 'react-icons/fa';
import styles from './IncidentListRow.module.css';
import { trackModernUIInteraction, trackViewFullDetails, trackMissingResolveDate, trackPastIncidentsExecSummaryClick } from '../../../../utils/modernUITracking';
import { useIncidentExpansionTracking } from '../hooks/useIncidentExpansionTracking';
import { buildSlackChannelUrl } from '../../../../constants/slackConstants';
import { formatTimestamp, isResolvedStatus } from '../../Status/statusUtils';
import ExecSummaryLink from './ExecSummaryLink';

const IncidentListRow = ({
  number,
  priority,
  shortDescription,
  status,
  slackChannel,
  incidentStart,
  incidentEnd,
  affectedBrand,
  execSummaryUrl,
  isExpanded,
  onToggle,
  section = 'active_incidents',
  newrelic = null,
}) => {
  // Format date with "Started" or "Resolved" prefix: "Started Jan 14, 3:10 PM"
  //
  // US2072496: a row is resolved when it sits in the Past Incidents section — the
  // resolved index only contains records that carry an incidentEnd, so membership
  // is proof of resolution — or when the status says so. The status check now goes
  // through the shared isResolvedStatus() predicate; this used to compare against
  // "RESOLVED"/"CLOSED", which the canonical "Resolve" never matched, so every
  // resolved row displayed its start date instead of its resolve date.
  //
  // A resolved row with no usable incidentEnd renders "Resolved —" rather than
  // silently falling back to the start time: that is a data defect operators should
  // see and investigate, and labelling a start time "Resolved" is the very bug
  // being fixed.
  //
  // "Usable" is keyed off the formatted result, not off `!incidentEnd`: a truthy
  // but unparseable timestamp formats to '' (formatTimestamp returns '' for an
  // invalid date), which would otherwise render a bare "Resolved " with no em dash
  // and emit no signal. AC #7's failure path covers "missing or malformed", so both
  // land in the same branch.
  const isResolved = section === 'past_incidents' || isResolvedStatus(status);
  const formattedResolveTime = isResolved
    ? formatTimestamp(incidentEnd, { relative: false, includeTime: true })
    : '';
  const missingResolveDate = isResolved && !formattedResolveTime;

  const getFormattedDate = () => {
    if (isResolved) {
      return `Resolved ${formattedResolveTime || '—'}`;
    }

    if (!incidentStart) return null;
    return `Started ${formatTimestamp(incidentStart, { relative: false, includeTime: true })}`;
  };

  // Emit the data-defect signal once per affected incident rather than per render,
  // so "Resolved —" is discoverable in NewRelic instead of relying on someone
  // noticing an em dash. Expected volume is zero.
  useEffect(() => {
    if (missingResolveDate) {
      trackMissingResolveDate(number, { surface: 'list_row', section }, newrelic);
    }
  }, [missingResolveDate, number, section, newrelic]);


  // Get priority class
  const getPriorityClass = () => {
    const priorityMap = {
      'EP1': styles.priorityEP1,
      'EP2': styles.priorityEP2,
      'EP3': styles.priorityEP3,
      'EP4': styles.priorityEP4
    };
    return priorityMap[priority] || styles.priorityEP4;
  };


  // Prop-driven Expand/Collapse tracking — closes the bug where IncidentSection's
  // keyboard arrow nav (IncidentSection.js:88-101) flips isExpanded directly
  // without going through onToggle, silently dropping tracking. The hook also
  // handles unmount-while-expanded so virtualized scroll / route change don't
  // drop the longest-view rows from the viewDurationMs histogram.
  useIncidentExpansionTracking({ number, isExpanded, source: 'row', section, newrelic });

  // Click/keyboard activation just delegates to the parent. Tracking is
  // owned by the effect-driven hook above.
  const handleToggle = () => {
    onToggle();
  };

  const formattedDate = getFormattedDate();

  return (
    <div
      className={`${styles.row} ${isExpanded ? styles.rowSelected : ''}`}
      onClick={handleToggle}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggle();
        }
      }}
      data-testid="incident-card"
    >
      {/* Priority Badge */}
      <div className={`${styles.priorityBadge} ${getPriorityClass()}`} data-testid="priority-badge">
        {priority}
      </div>

      {/* Title and Brand - Stacked */}
      <div className={styles.titleBrandWrapper}>
        <div className={styles.title}>{shortDescription}</div>
        <div className={styles.metadataRow}>
          {status && section !== 'past_incidents' && (
            <span className={styles.statusBadge}>{status}</span>
          )}
          {affectedBrand && (status && section !== 'past_incidents') && (
            <span className={styles.separator}>•</span>
          )}
          {affectedBrand && (
            <span className={styles.brandSection}>
              {affectedBrand}
            </span>
          )}
          {/* PRB number appended to the metadata line (current status + past incidents) */}
          {number && ((status && section !== 'past_incidents') || affectedBrand) && (
            <span className={styles.separator}>•</span>
          )}
          {number && (
            <span className={styles.brandSection}>{number}</span>
          )}
        </div>
      </div>

      {/* Slack Channel — hidden for past/resolved incidents */}
      {slackChannel && section !== 'past_incidents' && (
        <a
          href={buildSlackChannelUrl(slackChannel)}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.slackLink}
          onClick={(e) => {
            e.stopPropagation();
            trackModernUIInteraction('ModernUI.IncidentRow.SlackLink', {
              incidentId: number,
              slackChannel: slackChannel,
            }, newrelic);
          }}
        >
          {slackChannel}
        </a>
      )}

      {/* Date */}
      {formattedDate && (
        <div className={styles.dateSection}>
          {formattedDate}
        </div>
      )}

      {/* Exec Summary Slide icon (past incidents only; the slot is reserved so rows without a link align) */}
      {section === 'past_incidents' && (
        <ExecSummaryLink
          url={execSummaryUrl}
          reserveSpace
          onOpen={() => trackPastIncidentsExecSummaryClick({ incidentId: number, source: 'list_row' }, newrelic)}
        />
      )}

      {/* View Full Details */}
      <a
        href={`/problem/view/${number}`}
        className={styles.viewLink}
        onClick={(e) => {
          e.stopPropagation();
          trackViewFullDetails(number, { source: 'list_row', section }, newrelic);
        }}
        data-testid="incident-view-details-link"
      >
        View Full Details <FaExternalLinkAlt className={styles.viewLinkIcon} />
      </a>

      {/* Expand/Collapse Indicator */}
      <div className={`${styles.expandIcon} ${isExpanded ? styles.expandIconRotated : ''}`}>
        <FaChevronDown />
      </div>
    </div>
  );
};

IncidentListRow.propTypes = {
  number: PropTypes.string.isRequired,
  priority: PropTypes.string.isRequired,
  shortDescription: PropTypes.string.isRequired,
  status: PropTypes.string,
  slackChannel: PropTypes.string,
  incidentStart: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  incidentEnd: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  affectedBrand: PropTypes.string,
  execSummaryUrl: PropTypes.string,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  section: PropTypes.oneOf(['active_incidents', 'past_incidents']),
  newrelic: PropTypes.object,
};

export default IncidentListRow;
